import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOut, Phone, Envelope, IdentificationBadge, ShieldCheck, Camera } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const ROLE_LABEL = { orangtua: "Orang Tua", guru: "Guru", admin: "Administrator" };

export default function Profil() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const doLogout = async () => {
    await logout();
    toast.success("Berhasil keluar");
    navigate("/welcome");
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(f.type)) { toast.error("Format harus JPG atau PNG"); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Ukuran foto maksimal 3MB"); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/auth/me/photo", fd);
      setUser(data);
      toast.success("Foto profil berhasil diperbarui");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Profil Saya" back={false} />
      <div className="px-5 pt-6 flex flex-col items-center">
        <div className="relative">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name} className="w-24 h-24 rounded-full object-cover shadow-lg ring-4 ring-white" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <span className="font-heading text-4xl font-bold text-white">{user?.name?.[0] || "?"}</span>
            </div>
          )}
          <button data-testid="profile-photo-pick-btn" onClick={() => fileRef.current?.click()} disabled={saving}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shadow-lg border-2 border-white active:scale-90 transition-transform disabled:opacity-60" aria-label="Ubah foto profil">
            <Camera size={18} weight="fill" />
          </button>
          <input ref={fileRef} data-testid="profile-photo-input" type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} />
        </div>
        <h2 className="font-heading text-xl font-bold text-stone-800 mt-4">{user?.name}</h2>
        <span className="mt-1 text-xs font-semibold bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full flex items-center gap-1">
          <ShieldCheck size={14} weight="fill" className="text-primary" /> {ROLE_LABEL[user?.role]}
        </span>
        {saving && <p className="text-xs text-stone-400 mt-2">Menyimpan foto...</p>}
      </div>

      <div className="px-5 mt-6 space-y-2">
        {user?.email && <Row icon={Envelope} label="Email" value={user?.email} />}
        <Row icon={Phone} label="No. HP" value={user?.phone || "—"} />
        <Row icon={IdentificationBadge} label="Peran" value={ROLE_LABEL[user?.role]} />
      </div>

      <div className="px-5 mt-8">
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="w-full bg-destructive/10 text-destructive font-semibold py-4 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <SignOut size={20} weight="bold" /> Keluar
        </button>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon size={20} weight="duotone" className="text-primary" />
      </div>
      <div>
        <p className="text-xs text-stone-400">{label}</p>
        <p className="font-semibold text-stone-700 text-sm">{value}</p>
      </div>
    </div>
  );
}
